/*!
 * ua.button.toggle
 * Multi-state toggle button with optional settings launcher for larger option sets.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.button.toggle",
  title: "Button Toggle",
  description: "A reusable multi-state toggle button supporting icon, text, or icon+text display modes.",
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Logical control name for events and state.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Fallback title for accessibility."
    },
    mode: {
      title: "Display Mode",
      type: "string",
      description: "How each option should render: icon | text | icon-text.",
      list: ["icon", "text", "icon-text"],
      default: "icon-text"
    },
    style: {
      title: "Style",
      type: "string",
      description: "Button style classes.",
      default: "btn btn-outline-secondary btn-sm"
    },
    icon: {
      title: "Icon",
      type: "string",
      description: "Placeholder icon used when no option is currently selected."
    },
    options: {
      title: "Options",
      type: "array",
      of: "object",
      description: "Toggle options. Every row should provide { name, title, icon }"
    },
    value: {
      title: "Value",
      type: "string",
      description: "Current selected option name."
    },
    iconOnly: {
      title: "Icon Only",
      type: "boolean",
      description: "Shortcut for mode='icon'."
    },
    settingsIcon: {
      title: "Settings Icon",
      type: "string",
      description: "Icon class used by the settings button.",
      default: "bi bi-chevron-down"
    },
    "aria-label": {
      title: "ARIA Label",
      type: "string",
      description: "Optional explicit accessible name for icon-only or compact toggles."
    },
    "aria-labelledby": {
      title: "ARIA Labelled By",
      type: "string",
      description: "Optional element id(s) that provide the accessible name for the toggle group. When present, the fallback aria-label is suppressed."
    },
    "aria-describedby": {
      title: "ARIA Described By",
      type: "string",
      description: "Optional element id(s) that provide additional accessible description for the toggle group."
    }
  }
});

function normalizeOption(option, index) {
  var row = (option && typeof option === "object") ? option : {};
  var hasExplicitName = Object.prototype.hasOwnProperty.call(row, "name") || Object.prototype.hasOwnProperty.call(row, "id") || Object.prototype.hasOwnProperty.call(row, "value");
  var name = hasExplicitName ? (row.name || row.id || row.value || "").toString().trim() : (("option-" + index));
  var title = (row.title || row.label || name || ("Option " + (index + 1))).toString().trim();
  var icon = (row.icon || "bi-circle").toString().trim();
  var mode = (row.mode || "").toString().trim().toLowerCase();
  var className = (row.className || "").toString().trim();

  return {
    name: name,
    title: title,
    icon: icon,
    mode: mode,
    className: className
  };
}

function normalizeIconClass(icon) {
  var iconText = (icon || "").toString().trim();
  if (!iconText) return "";
  if (iconText.indexOf("bi ") === 0) return iconText;
  if (iconText.indexOf("bi-") === 0) return "bi " + iconText;
  return iconText;
}

function renderToggleLabelNode(option, mode) {
  var optionMode = (option.mode || "").toString().trim().toLowerCase();
  var displayMode = optionMode || mode;
  var iconClass = normalizeIconClass(option.icon);
  var title = option.title || option.name;

  if (displayMode === "icon") {
    return iconClass ? { n: "i", c: iconClass } : "";
  }

  if (displayMode === "text") {
    return title;
  }

  if (!iconClass) return title;
  return [
    { n: "i", c: iconClass },
    { n: "span", i: title }
  ];
}

exports.render = function(options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || name || "Toggle").toString();
  var explicitAriaLabel = (options["aria-label"] || options.ariaLabel || "").toString().trim();
  var labelledBy = (options["aria-labelledby"] || options.ariaLabelledby || "").toString().trim();
  var describedBy = (options["aria-describedby"] || options.ariaDescribedby || "").toString().trim();
  var baseMode = options.iconOnly === true
    ? "icon"
    : ((options.mode || "icon-text").toString().trim().toLowerCase() || "icon-text");
  var style = (options.style || "btn btn-outline-secondary btn-sm").toString().trim();
  var settingsIcon = normalizeIconClass(options.settingsIcon || "bi bi-chevron-down") || "bi bi-chevron-down";
  var placeholderIcon = normalizeIconClass(options.icon || "");

  var raw = options.options instanceof Array ? options.options : [];
  var rows = [];
  for (var i = 0; i < raw.length; i++) {
    var normalized = normalizeOption(raw[i], i);
    if (!normalized.name && normalized.name !== "") continue;
    rows.push(normalized);
  }

  if (rows.length === 0) {
    rows.push({ name: "default", title: "Default", icon: "bi-circle", mode: "", className: "" });
  }

  var requestedValue = (options.value || "").toString().trim();
  var current = {
    name: "",
    title: title,
    icon: placeholderIcon,
    mode: "",
    className: ""
  };
  var hasMatch = false;
  for (var r = 0; r < rows.length; r++) {
    if (rows[r].name === requestedValue) {
      current = rows[r];
      hasMatch = true;
      break;
    }
  }
  if (!hasMatch && requestedValue && rows.length > 0) {
    current = rows[0];
  }

  var optionsJson = "[]";
  try {
    optionsJson = JSON.stringify(rows);
  } catch (error) {
    optionsJson = "[]";
  }

  var showSettings = rows.length > 2;
  var groupClass = "ua-button-toggle d-inline-flex align-items-center gap-1";

  return {
    n: "div",
    c: groupClass,
    role: "group",
    "aria-label": labelledBy ? undefined : (explicitAriaLabel || title),
    "aria-labelledby": labelledBy || undefined,
    "aria-describedby": describedBy || undefined,
    "data-ua-button-toggle": "true",
    "data-toggle-name": name,
    "data-toggle-mode": baseMode,
    "data-toggle-value": current.name,
    "data-toggle-placeholder-title": title,
    "data-toggle-main-aria-label-base": explicitAriaLabel || title,
    "data-toggle-placeholder-icon": placeholderIcon,
    "data-toggle-options": optionsJson,
    i: [
      {
        n: "button",
        type: "button",
        c: style + " d-inline-flex align-items-center gap-1",
        title: title + ": " + current.title,
        "aria-label": (explicitAriaLabel || title) + ": " + current.title,
        "data-toggle-main": "true",
        i: [
          {
            n: "span",
            "data-toggle-label": "true",
            i: renderToggleLabelNode(current, baseMode)
          },
          { n: "i", c: "bi bi-chevron-down small", "aria-hidden": "true" }
        ]
      },
      showSettings ? {
        n: "button",
        type: "button",
        c: style,
        title: title + " options",
        "aria-label": title + " options",
        "data-toggle-settings": "true",
        i: { n: "i", c: settingsIcon }
      } : ""
    ]
  };
};
